import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CaretRightIcon } from '@phosphor-icons/react';

import {
  formatCount,
  formatDayLabel,
  formatTokens,
  formatUsd,
  useTeamUsage,
  type DailyUsage,
  type TeamUsage,
} from '@/api-models/usage';
import { Card } from '@/ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from '@/ui/Table';
import { Spinner } from '@/ui/Spinner';
import { teamPath } from '@/contexts/space-context';
import classNames from '@/utils/classnames';
import { buildSeries, stackDay, type Series } from './series';

/**
 * The ledger proper: one row per day, newest first, expanding to the members and
 * sessions that made up that day.
 *
 * The drill-down re-reads the same endpoint for a one-day window rather than
 * shipping every day's session list up front — a month of sessions is a payload
 * nobody scrolls, and the day view is the same aggregation, so the two can't
 * disagree.
 */
export function UsageLedger({ usage, teamId, userId }: { usage: TeamUsage; teamId: string; userId: string | null }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const series = buildSeries(usage);

  // Newest first: the interesting day is almost always the most recent one.
  const days = [...usage.daily].reverse();
  const peak = Math.max(0, ...days.map((d) => d.total_micro_usd));

  return (
    <Card radius="xl" padding="none" className="overflow-hidden">
      <div className="border-b border-line-subtle px-4 py-3">
        <h3 className="card-heading">Daily ledger</h3>
        <p className="mt-0.5 text-xs text-fg-tertiary">
          Expand a day to see the members and sessions it was charged to.
        </p>
      </div>

      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell className="w-8" />
            <TableHeaderCell>Day</TableHeaderCell>
            <TableHeaderCell className="text-right">Spend</TableHeaderCell>
            <TableHeaderCell>Split</TableHeaderCell>
            <TableHeaderCell className="text-right">Requests</TableHeaderCell>
            <TableHeaderCell className="text-right">Tokens</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {days.map((day) => (
            <DayRows
              key={day.date}
              day={day}
              series={series}
              peak={peak}
              teamId={teamId}
              userId={userId}
              isExpanded={expanded === day.date}
              onToggle={() => setExpanded(expanded === day.date ? null : day.date)}
            />
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function DayRows({
  day,
  series,
  peak,
  teamId,
  userId,
  isExpanded,
  onToggle,
}: {
  day: DailyUsage;
  series: Series[];
  peak: number;
  teamId: string;
  userId: string | null;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const empty = day.total_micro_usd === 0 && day.request_count === 0 && day.tool_request_count === 0;
  const stack = stackDay(day.per_user, series);

  return (
    <>
      <TableRow
        clickable={!empty}
        onClick={empty ? undefined : onToggle}
        className={classNames(empty && 'text-fg-muted')}
      >
        <TableCell className="pr-0">
          {!empty && (
            <CaretRightIcon
              size={12}
              weight="bold"
              className={classNames('text-fg-muted transition-transform', isExpanded && 'rotate-90')}
            />
          )}
        </TableCell>
        <TableCell className="text-sm text-fg">{formatDayLabel(day.date, { withYear: true })}</TableCell>
        <TableCell className="text-right">
          <span className={classNames('font-medium tabular-nums', empty ? 'text-fg-muted' : 'text-fg')}>
            {formatUsd(day.total_micro_usd)}
          </span>
        </TableCell>
        <TableCell>
          <DaySplit stack={stack} series={series} total={day.total_micro_usd} peak={peak} />
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {formatCount(day.request_count + day.tool_request_count)}
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {formatTokens(day.prompt_tokens + day.completion_tokens)}
        </TableCell>
      </TableRow>

      {isExpanded && (
        <TableRow>
          <TableCell colSpan={6} className="bg-surface-3/40 p-0">
            <DayDetail date={day.date} teamId={teamId} userId={userId} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/**
 * The day's stack as a horizontal bar, scaled against the window's busiest day so
 * rows are comparable to each other rather than each filling its own cell.
 */
function DaySplit({ stack, series, total, peak }: { stack: number[]; series: Series[]; total: number; peak: number }) {
  if (total <= 0 || peak <= 0) return <span className="text-xs text-fg-muted">—</span>;

  return (
    <span
      className="flex h-1.5 w-full max-w-56 overflow-hidden rounded-full"
      style={{ width: `${(total / peak) * 100}%` }}
    >
      {stack.map((value, i) =>
        value > 0 ? (
          <span
            key={series[i]!.key}
            title={`${series[i]!.label}: ${formatUsd(value)}`}
            // The 2px surface gap that separates touching marks, as a border.
            className="h-full border-r-2 border-surface-2 last:border-r-0"
            style={{ width: `${(value / total) * 100}%`, backgroundColor: series[i]!.color }}
          />
        ) : null,
      )}
    </span>
  );
}

/** One day, re-fetched as its own single-day window. */
function DayDetail({ date, teamId, userId }: { date: string; teamId: string; userId: string | null }) {
  const { usage, isLoading } = useTeamUsage(teamId, { start: date, end: date, userId });

  if (isLoading && !usage) {
    return (
      <div className="flex items-center gap-2 px-6 py-5 text-xs text-fg-tertiary">
        <Spinner size={4} /> Loading {formatDayLabel(date)}…
      </div>
    );
  }
  if (!usage) return <p className="px-6 py-5 text-xs text-fg-tertiary">Could not load this day.</p>;

  return (
    <div className="grid gap-8 px-6 py-5 lg:grid-cols-2">
      <DetailList
        title="By member"
        rows={usage.by_user.map((u) => ({
          key: u.user_id ?? 'unattributed',
          label: u.name ?? 'Unattributed',
          sub: `${formatCount(u.request_count + u.tool_request_count)} requests · ${formatCount(
            u.session_count,
          )} ${u.session_count === 1 ? 'session' : 'sessions'}`,
          micro: u.total_micro_usd,
        }))}
        empty="No member spend on this day."
      />
      <DetailList
        title="By session"
        rows={usage.by_session.map((s) => ({
          key: s.session_id,
          label: s.name ?? 'Untitled session',
          sub: [s.user_name, s.repository_name].filter(Boolean).join(' · ') || s.session_id,
          micro: s.total_micro_usd,
          to: teamPath(teamId, `/sessions/${s.session_id}`),
        }))}
        empty="No session spend on this day."
      />
    </div>
  );
}

interface DetailRow {
  key: string;
  label: string;
  sub: string;
  micro: number;
  to?: string;
}

function DetailList({ title, rows, empty }: { title: string; rows: DetailRow[]; empty: string }) {
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-2xs font-semibold uppercase tracking-wider text-fg-muted">{title}</h4>
      {rows.length === 0 ? (
        <p className="text-xs text-fg-tertiary">{empty}</p>
      ) : (
        <ul className="flex list-none flex-col gap-1.5 p-0">
          {rows.map((row) => {
            const body = (
              <>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-sm text-fg">{row.label}</span>
                  <span className="truncate text-xs text-fg-muted">{row.sub}</span>
                </span>
                <span className="shrink-0 text-sm font-medium tabular-nums text-fg">{formatUsd(row.micro)}</span>
              </>
            );
            return (
              <li key={row.key}>
                {row.to ? (
                  <Link
                    to={row.to}
                    className="flex items-center justify-between gap-4 rounded-md px-2 py-1 text-fg no-underline transition-colors hover:bg-surface-3"
                  >
                    {body}
                  </Link>
                ) : (
                  <div className="flex items-center justify-between gap-4 px-2 py-1">{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
